import moment from 'moment';
import isEqual from 'react-fast-compare';

import * as analytics from 'analytics/Analytics';
import {NAV_ANALYTICS_ACTIONS} from 'constants/analytics-products';
import {Section} from 'constants/NavConstants';
import {SearchSection} from 'graphql-client/__generated/graphqlGlobalTypes';
import {stringifyFilters} from 'nav/sidepanel/listing/SearchURLs';
import {getSelectedPageSize} from 'nav/sidepanel/PageSizeUtil';
import {openListing} from 'nav/sidepanel/SidepanelReduxActions';
import {SavedSearchData} from 'notebook/mediocre/MediocreConstants';
import {getStore} from 'redux-stores/Store';
import {isMultiQueryBLAST} from 'search/blast/BLASTUtil';
import * as FilterUtil from 'search/FilterUtil';
import {SavedSearchesQuery} from 'search/saved-searches/__generated/SavedSearchesQuery';
import {switchToSavedSearchProjectFolderWithParams} from 'search/saved-searches/switchToSavedSearchProjectFolder';
import {switchToSavedSearchRegistryForRegistrySidepanelWithParams} from 'search/saved-searches/switchToSavedSearchRegistryForRegistrySidepanel';
import {switchToSavedSearchRegistryForStorageSidepanelWithParams} from 'search/saved-searches/switchToSavedSearchRegistryForStorageSidepanel';
import {
  BLASTFilter,
  DatetimeFilter,
  DEPRECATED_FILTER_KEYS,
  Filter,
  FilterKey,
  FILTERS,
  isDatetimeFilter,
  isInStudyFilter,
  isStandardDateFilter,
  isStandardDatetimeFilter,
  isStandardFilter,
  MultiValueFilter,
  OldOwnerFilter,
  Operator,
  SearchParams,
  SearchType,
  SingleValueFilter,
  Sort,
  StandardFilter,
  StandardFilterDataType,
  isMultiValueFilter,
  isFieldFilter,
  isTextFieldType,
} from 'search/SearchConstants';
import {USER_EDIT_DATE_FORMAT} from 'util/DateConstants';
import {readableTime} from 'util/DateUtil';
import sortedBy from 'util/sortedBy';

type GQLSavedSearchParams = SavedSearchesQuery['savedSearches'][number]['searchParams'];

export type NonPaginatedSearchParams = Pick<SearchParams, 'sorts' | 'filters' | 'query' | 'groupBy'>;

export type SavedSearch = NonNullable<SavedSearchesQuery['savedSearches'][number]>;

interface LegacyDateFilter {
  value: {toDate: string; fromDate: string};
}

interface LegacyNullableEntityLinkFilter {
  value: Array<string>;
  includeNull: boolean;
}

export enum RenamedFilterKey {
  RECIPE_RUN_TASKS = 'RECIPE_RUN_TASKS',
  ASSAY_RUN_TASKS = 'ASSAY_RUN_TASKS',
}

const DEPRECATED_FILTER_KEYS_TO_NEW_FILTER_KEYS: Record<RenamedFilterKey, FilterKey> = {
  [RenamedFilterKey.RECIPE_RUN_TASKS]: FilterKey.RECIPE_CONDITION_REPLICATE_RUN_TASKS,
  [RenamedFilterKey.ASSAY_RUN_TASKS]: FilterKey.ASSAY_CONDITION_REPLICATE_RUN_TASKS,
};

interface RenamedFilter {
  key: RenamedFilterKey;
}

function hasFilterBeenRenamed(filter: {key: string}): filter is RenamedFilter {
  return DEPRECATED_FILTER_KEYS_TO_NEW_FILTER_KEYS[filter.key as RenamedFilterKey] != null;
}

export function handleRenamedFilters(filter: RenamedFilter): Filter {
  const newKey = DEPRECATED_FILTER_KEYS_TO_NEW_FILTER_KEYS[filter.key];
  if (newKey != null) {
    return {
      ...filter,
      key: DEPRECATED_FILTER_KEYS_TO_NEW_FILTER_KEYS[filter.key],
    } as Filter;
  }
  throw new Error(`Filter ${filter.key} has not been renamed.`);
}

export function convertSavedFiltersToCurrentFormat(filters: Array<Filter>): Array<Filter> {
  return filters.map((filter) => {
    if (hasFilterBeenRenamed(filter)) {
      return handleRenamedFilters(filter);
    }
    // This modification covers changes to ModifiedAt and CreatedAt filters which previously
    // used dates as the range for the filter, but now use datetimes.  We adjust the toDate here
    // in order to match date comparison functionality that is not present in the datetime version.
    if (
      isDatetimeFilter(filter) &&
      (filter.value.hasOwnProperty('fromDate') || filter.value.hasOwnProperty('toDate'))
    ) {
      const toDate = (filter as unknown as LegacyDateFilter).value.toDate;
      const toDatetime = toDate != null ? moment(toDate).add(1, 'days').format(USER_EDIT_DATE_FORMAT) : null;

      filter = {
        ...filter,
        value: {fromDatetime: (filter as unknown as LegacyDateFilter).value.fromDate, toDatetime},
      };
    } else if (isInStudyFilter(filter) && typeof filter.value === 'string') {
      const scalarValue = (filter as unknown as SingleValueFilter).value;

      filter = {
        operator: (filter as unknown as SingleValueFilter).operator,
        key: FilterKey.IN_STUDY,
        value: [scalarValue!],
      };
    } else if (filter.key === FilterKey.CONTAINER_CONTENT_SCHEMA && !Array.isArray(filter.value)) {
      // CONTAINER_CONTENT_SCHEMA moved from SINGLE_VALUE_FILTER_KEYS to MULTI_VALUE_FILTER_KEYS,
      // so saved searches created before that change still have a scalar value.
      // TODO(BNCH-161293): Remove this adapter (and its regression tests) once the backfill
      // script has normalized all stored saved searches to the multi-value shape.
      const scalarValue = (filter as unknown as SingleValueFilter).value;
      filter = {
        key: FilterKey.CONTAINER_CONTENT_SCHEMA,
        operator: Operator.IS_ONE_OF,
        value: scalarValue != null ? [scalarValue] : [],
      } as MultiValueFilter;
    } else if (filter.key === FilterKey.REVIEW_LIFECYCLE) {
      if (filter.operator === Operator.IS) {
        if (filter.value) {
          filter = {
            ...filter,
            operator: Operator.IS_ONE_OF,
            value: [filter.value],
          } as StandardFilter;
        } else {
          filter = {
            ...filter,
            operator: Operator.IS_NOT_EMPTY,
            value: null,
          } as StandardFilter;
        }
      }
    } else if (filter.key === FilterKey.PROTEIN_AMINO_ACIDS && !filter.hasOwnProperty('operator')) {
      filter = {
        ...filter,
        operator: Operator.INCLUDES_TEXT,
      };
    } else if (isStandardDatetimeFilter(filter) && !filter.hasOwnProperty('operator')) {
      filter = {
        ...filter,
        operator: Operator.BETWEEN,
        value: {
          to: filter.value ? (filter as unknown as DatetimeFilter).value.toDatetime : null,
          from: filter.value ? (filter as unknown as DatetimeFilter).value.fromDatetime : null,
        },
      };
    } else if (
      filter.key === FilterKey.OWNER &&
      (filter.value?.hasOwnProperty('userIds') || filter.value?.hasOwnProperty('organizationIds'))
    ) {
      const value = (filter as unknown as OldOwnerFilter).value;
      filter = {
        ...filter,
        value: [...(value.userIds ?? []), ...(value.organizationIds ?? [])],
      };
    } else if (
      isFieldFilter(filter) &&
      isTextFieldType(filter.fieldType) &&
      [Operator.INCLUDES_TEXT, Operator.EXCLUDES_TEXT].includes(filter.operator) &&
      typeof filter.value === 'string'
    ) {
      filter = {
        ...filter,
        value: [filter.value],
      };
    } else if (isStandardDateFilter(filter) && filter.operator === Operator.IS_EMPTY && filter.value) {
      filter = {
        ...filter,
        operator: Operator.IS_EMPTY,
        value: null,
      };
    } else if (isStandardDateFilter(filter) && filter.operator === Operator.IS) {
      filter = {
        ...filter,
        operator: Operator.BETWEEN,
        value: {
          to: filter.value ? (filter as unknown as LegacyDateFilter).value.toDate : null,
          from: filter.value ? (filter as unknown as LegacyDateFilter).value.fromDate : null,
        },
      };
    } else if (
      // IS and IS_NOT operators were originally included on the client for SINGLE_VALUE_ENTITY_LINK
      // fields, but we really don't want to display them since they are redundant with IS_ONE_OF and
      // IS_NONE_OF. However, we need to support parsing them from saved searches for backwards compatibility
      // since we released a filter with this data type before removing them, so people may have saved
      // searches - especially since IS was the default.
      isStandardFilter(filter) &&
      FILTERS[filter.key].dataType === StandardFilterDataType.SINGLE_VALUE_ENTITY_LINK &&
      [Operator.IS, Operator.IS_NOT].includes(filter.operator)
    ) {
      filter = {
        ...filter,
        operator: filter.operator === Operator.IS ? Operator.IS_ONE_OF : Operator.IS_NONE_OF,
        value: [filter.value],
      } as StandardFilter;
    } else if (
      // Some filters that were set as multi value filters in the past defaulted to using only the IS_ONE_OF operator,
      // but if it's a multi value entity link filter, then HAS_ONE_OF should be the corresponding operator to use.
      // This is the case for RequestAssigneeFilter, and maybe some more.
      isStandardFilter(filter) &&
      FILTERS[filter.key].dataType === StandardFilterDataType.MULTI_VALUE_ENTITY_LINK &&
      filter.operator === Operator.IS_ONE_OF
    ) {
      filter = {
        ...filter,
        operator: Operator.HAS_ONE_OF,
      };
    } else if (
      isStandardFilter(filter) &&
      [FilterKey.PROCESS_RESPONSIBLE_TEAM, FilterKey.PROCESS_TASK_ASSIGNEE].includes(filter.key) &&
      filter.hasOwnProperty('includeNull')
    ) {
      const newValue = [...(filter.value as Array<string | null>)];
      if ((filter as unknown as LegacyNullableEntityLinkFilter).includeNull) {
        newValue.push(null);
      }
      // We want to remove the includeNull property, so don't use the spread operator. to clone the
      // key and operator.
      filter = {
        key: filter.key,
        operator: filter.operator,
        value: newValue,
      } as StandardFilter;
    } else if (isMultiValueFilter(filter) && filter.key === FilterKey.TYPES) {
      // TODO(BNCH-103268): Remove this convertion of procedure to assay and recipe once the backfill script
      // to update existing saved searches has run to prevent backwards compatibility issues for saved procedure filters
      filter = FilterUtil.replaceProcedureTypeFilter(filter);
    }
    return filter;
  });
}

export function convertFromGraphqlSavedSearchParams(params: GQLSavedSearchParams): NonPaginatedSearchParams {
  const nonDeprecatedFilters = params.filtersJson.filter(
    (filterJson: Filter) => !DEPRECATED_FILTER_KEYS.has(filterJson.key)
  );

  const updatedFilters = convertSavedFiltersToCurrentFormat(nonDeprecatedFilters);

  let cleanedSorts: Array<Sort> = [];
  if (params.sorts && params.sorts.length >= 1) {
    cleanedSorts = params.sorts.map(({reverse, key}) => ({reverse, key}));
  }

  let groupByOrNull = params.groupField
    ? {
        childLimit: 0,
        childOffset: 0,
        groupField: params.groupField,
      }
    : null;

  const blastFilter = FilterUtil.getFilterByKey(updatedFilters, FilterKey.BLAST) as BLASTFilter;
  if (blastFilter && isMultiQueryBLAST(blastFilter)) {
    groupByOrNull = {
      childLimit: getSelectedPageSize(),
      childOffset: 0,
      groupField: params.groupField as string,
    };
  }

  return {
    sorts: cleanedSorts,
    filters: updatedFilters,
    query: params.query,
    groupBy: groupByOrNull,
  };
}

export function savedSearchParamsAreEqual(
  params1: NonPaginatedSearchParams,
  params2: NonPaginatedSearchParams
): boolean {
  const sortedFilters1 = sortedBy(params1.filters, [(filter) => stringifyFilters([filter])]);
  const sortedFilters2 = sortedBy(params2.filters, [(filter) => stringifyFilters([filter])]);
  return (
    isEqual(params1.sorts, params2.sorts) &&
    params1.query === params2.query &&
    isEqual(sortedFilters1, sortedFilters2) &&
    isEqual(params1.groupBy, params2.groupBy)
  );
}

export function getMetadataText(
  description: string,
  ownerName: string,
  modifiedAt: string,
  disabled: boolean | undefined,
  newLine?: boolean,
  requiredOwnerId?: string
): string {
  if (disabled) {
    if (requiredOwnerId) {
      return 'You can not select this search. You can only select searches that are owned by the owner of the template.';
    } else {
      return 'You lack permissions to run this search. Ask your administrator for help';
    }
  }

  const time = readableTime(modifiedAt, {showTime: false});
  if (description) {
    if (newLine) {
      return `Updated ${time} by ${ownerName}\n${description}`;
    } else {
      return `Updated ${time} by ${ownerName} | ${description}`;
    }
  } else {
    return `Updated ${time} by ${ownerName}`;
  }
}

export function triggerSearchFromInternalLink(savedSearchData: SavedSearchData): void {
  const searchParams = convertFromGraphqlSavedSearchParams(savedSearchData.searchParams);
  if (savedSearchData.section === SearchSection.PROJECT_ITEMS) {
    switchToSavedSearchProjectFolderWithParams(searchParams, true);
  } else if (savedSearchData.section === SearchSection.REGISTRIES) {
    switchToSavedSearchRegistryForRegistrySidepanelWithParams(searchParams, true);
  } else if (savedSearchData.section === SearchSection.STORAGE) {
    switchToSavedSearchRegistryForStorageSidepanelWithParams(searchParams);
  } else {
    getStore().dispatch(
      openListing(SAVED_SEARCH_SECTION_TO_NAV_SECTION[savedSearchData.section], {
        offset: 0,
        limit: getSelectedPageSize(),
        ...searchParams,
      })
    );
  }
  analytics.trackEvent(NAV_ANALYTICS_ACTIONS.OPENED_SAVED_SEARCH, {
    section: savedSearchData.section,
    api_id: savedSearchData.id,
    entry_id: savedSearchData.fileId,
  });
}

export const SAVED_SEARCH_SECTION_TO_NAV_SECTION: {[key in SearchSection]: Section} = {
  [SearchSection.ANALYTICS]: Section.ANALYTICS,
  [SearchSection.GLOBAL]: Section.SEARCH,
  [SearchSection.CONNECTIONS]: Section.CONNECTIONS,
  [SearchSection.JELLYFISH]: Section.JELLYFISH,
  [SearchSection.LIBRARY]: Section.TEMPLATE_COLLECTIONS,
  [SearchSection.PROCESSES]: Section.PROCESSES,
  [SearchSection.PROJECT_ITEMS]: Section.PROJECT_ITEMS,
  [SearchSection.REGISTRIES]: Section.REGISTRIES,
  [SearchSection.REQUESTS]: Section.REQUESTS,
  [SearchSection.REQUESTS_V2]: Section.REQUESTS_V2_SUBMISSIONS,
  [SearchSection.STORAGE]: Section.STORAGE,
  [SearchSection.STUDIES]: Section.STUDIES,
};

export const NAV_SECTION_TO_SAVED_SEARCH_SECTION: {[key in Section]?: SearchSection} = {
  [Section.ANALYTICS]: SearchSection.ANALYTICS,
  [Section.SEARCH]: SearchSection.GLOBAL,
  [Section.CONNECTIONS]: SearchSection.CONNECTIONS,
  [Section.JELLYFISH]: SearchSection.JELLYFISH,
  [Section.PROCESSES]: SearchSection.PROCESSES,
  [Section.PROJECT_ITEMS]: SearchSection.PROJECT_ITEMS,
  [Section.REGISTRIES]: SearchSection.REGISTRIES,
  [Section.REQUESTS]: SearchSection.REQUESTS,
  [Section.STORAGE]: SearchSection.STORAGE,
  [Section.STUDIES]: SearchSection.STUDIES,
  [Section.REQUESTS_V2_SUBMISSIONS]: SearchSection.REQUESTS_V2,
  [Section.TEMPLATE_COLLECTIONS]: SearchSection.LIBRARY,
};

export const SAVED_SEARCH_SECTION_TO_SEARCH_TYPE: {[key in SearchSection]: SearchType} = {
  [SearchSection.ANALYTICS]: SearchType.ANALYSES_AND_ANALYTICS_DASHBOARDS,
  [SearchSection.GLOBAL]: SearchType.GLOBAL,
  [SearchSection.CONNECTIONS]: SearchType.CONNECTIONS,
  [SearchSection.JELLYFISH]: SearchType.JELLYFISH,
  [SearchSection.LIBRARY]: SearchType.TEMPLATE_COLLECTION,
  [SearchSection.PROCESSES]: SearchType.PROCESS_MANAGEMENT,
  [SearchSection.PROJECT_ITEMS]: SearchType.FOLDERS_AND_ITEMS,
  [SearchSection.REGISTRIES]: SearchType.BIOENTITIES,
  [SearchSection.REQUESTS]: SearchType.REQUEST,
  [SearchSection.REQUESTS_V2]: SearchType.REQUEST_V2_DASHBOARD,
  [SearchSection.STORAGE]: SearchType.STORABLE,
  [SearchSection.STUDIES]: SearchType.STUDIES,
};
